import Head from 'next/head';
import { useRouter } from 'next/router';
import { useRegiao, useSiteRegiao } from '../components/context/Info';
import { getRegiaoSlugFromPath } from '../lib/region';
import { buildRealEstateAgentSchema, buildWebsiteSchema } from './schema';
import { buildCanonical, pickCanonicalHost } from './url';

// titleOverride/descriptionOverride (opcionais): páginas que têm SEO próprio e ainda usam o SiteHead para o
// resto (canonical, og:image, JSON-LD) — ex.: /menu/<nome> (utils/seoPaginaMenu). Sem eles, nada muda.

const LOGO_OG_FUNDO_PRETO = new Set<number>([2558, 2681]);

export default function SiteHead({ pagina, allDataHome, canonicalHost, titleOverride, descriptionOverride } : { pagina: any, allDataHome?: any, canonicalHost?: string, titleOverride?: string, descriptionOverride?: string }) {
  const router = useRouter();
  // MULTI-FILIAIS: o schema/JSON-LD (telefone, e-mail) sai com o contato da
  // filial ativa. Título/description da home `/` continuam os GLOBAIS do site —
  // só `/r/<slug>` usa o seoTitle/seoDescription da região (regiaoSeoAtiva).
  const site = useSiteRegiao(allDataHome?.site);
  // MULTI-REGIÃO: na home por região (/r/<slug>) o title/description vêm da região
  // (seoTitle/seoDescription com fallback). O canonical já sai certo (asPath=/r/<slug>).
  // Só no path /r/ — região por ?regiao=/cookie NÃO muda o SEO das outras páginas.
  const { regiaoAtiva } = useRegiao();
  const slugRegiaoPath = getRegiaoSlugFromPath(router.asPath || '');
  const regiaoSeoAtiva = slugRegiaoPath && regiaoAtiva?.slug === slugRegiaoPath ? regiaoAtiva : null;

  const runtimeHost = typeof window !== 'undefined' ? window.location.host : '';
  // SEO: domínio primário (site.site) primeiro; só cai pro host requisitado se ausente.
  const host = pickCanonicalHost(site?.site, canonicalHost || runtimeHost);
  const canonicalUrl = buildCanonical(host, router.asPath || '/');
  const baseUrl = buildCanonical(host, '/').replace(/\/$/, '');

  const activeBanners = allDataHome?.allBanners.filter((item: { ativo: boolean; }) => item.ativo);
  const logoImage = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : site?.url_icon;
  // Logo sobre fundo PRETO na prévia do link (WhatsApp/redes): PNG transparente aparecia em branco. cs2558; cs2681 (03/11).
  // 2238 (SD-0062, 03/11/2026): imagem própria 1200×630 para a prévia da home (site sem banner, topo em vídeo).
  const ogImage = LOGO_OG_FUNDO_PRETO.has(Number(site?.id)) && logoImage ? `${baseUrl}/api/og-image?url=${encodeURIComponent(logoImage)}&bg=black` : site?.id == 2204 ? site?.url_icon : site?.id == 2238 ? "https://fotos2.fra1.cdn.digitaloceanspaces.com/sites/2238/og-home.jpg" : site?.id == 1899 ? "https://imob.sobressai.com.br/Banner/1899/11879.jpg" : site?.id == 1616 ? "https://imob.sobressai.com.br/Banner/1616/11880.jpg" : activeBanners?.[0]?.url || logoImage;

  const tiposImoveis = allDataHome?.tipos.map((tipo: { nome: string; }) => tipo.nome).join(', ');
  const cidades = allDataHome?.cidades.map((cidade: { nome: string; }) => cidade.nome).join(', ');
  const transacoes = site?.transacoes.join(' e ');

  const nomeEmpresa = site?.Dados?.empresa || 'Imobiliária';

  const defaultTitle =
    titleOverride ||
    (regiaoSeoAtiva
      ? regiaoSeoAtiva.seoTitle ||
        `Imóveis em ${regiaoSeoAtiva.nome} | ${site?.titulo || nomeEmpresa}`
      : '') ||
    site?.titulo || `${nomeEmpresa} - Imóveis em ${cidades}`;

  const defaultDescription =
    descriptionOverride ||
    (regiaoSeoAtiva ? regiaoSeoAtiva.seoDescription || '' : '') ||
    site?.meta_description ||
    `Encontre ${tiposImoveis} para ${transacoes} nas regiões de ${cidades}. Atendimento personalizado, documentação 100% online e aprovação rápida. Fale com a ${nomeEmpresa}!`;

  const keywords =
    site?.meta_keywords ||
    `imóveis, imobiliária, ${cidades}, ${transacoes}, ${tiposImoveis}`;

  return (
    <Head>
      <title>{defaultTitle}</title>
      <meta name="description" content={defaultDescription} />
      <meta name="keywords" content={keywords} />

      <meta property="og:title" content={defaultTitle} />
      <meta property="og:description" content={defaultDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:type" content={pagina === 'home' ? 'website' : 'article'} />
      <link rel="canonical" href={canonicalUrl} key="canonical" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={defaultTitle} />
      <meta name="twitter:description" content={defaultDescription} />
      <meta name="twitter:image" content={ogImage} />

      {site?.url_icon ? <link rel="icon" href={site.url_icon} /> : null}
      <link rel="dns-prefetch" href="https://fotos.sobressai.com.br" />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildRealEstateAgentSchema(site, baseUrl)),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildWebsiteSchema(site, baseUrl)),
        }}
      />
    </Head>
  );
}
